import { booleanPointInPolygon, buffer, feature } from '@turf/turf';
import exifr from 'exifr';
import ImageBlobReduce from 'image-blob-reduce';
import JSZip from 'jszip';
import type { CreateGeoItemInput, UploadedGeoItemPhoto } from '~/api/projects2';

// Formats every consumer of a geo-item photo can decode: browsers, the mobile app, and sharp in the
// PDF export. HEIC and TIFF are deliberately absent: sharp's bundled libheif has no HEVC decoder, so
// HEIC exports render a "could not be loaded" box, and neither format decodes in a canvas, which the
// thumbnail below needs.
const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp']);

// Camera formats we knowingly turn away, reported as skipped rather than dropped in silence: HEIC is
// the iPhone default, so a zip straight off a phone can be entirely these.
const UNSUPPORTED_IMAGE_EXTENSIONS = new Set(['heic', 'heif', 'tif', 'tiff']);

// Extraction is all in-browser, so an oversized archive or a zip bomb can exhaust memory and freeze
// the page. These caps bound the work before any blob is materialized. Real imports are hundreds of
// photos / a few hundred MB; the ceilings sit well above that.
const MAX_ZIP_ENTRIES = 20000;
export const MAX_IMAGE_COUNT = 5000;
const MAX_IMAGE_BYTES = 200 * 1024 * 1024;
const MAX_TOTAL_BYTES = 2 * 1024 * 1024 * 1024;

// Thrown when a selection exceeds a limit; its message is user-facing (handleFiles surfaces it).
export class ImportLimitError extends Error {}

export type ZipImage = { name: string; blob: Blob };
// `oversized` names are image entries dropped for exceeding MAX_IMAGE_BYTES and `unsupported` ones
// dropped for their format, so the caller can list them as skipped rather than silently losing them.
export type ZipReadResult = { images: ZipImage[]; oversized: string[]; unsupported: string[] };

// A whole dropped selection: zips and loose files merged. `ignored` names non-image files the user
// picked (a stray .txt, a spreadsheet), listed as skipped because they chose them deliberately —
// the same files inside a zip are passed over in silence. `unreadable` names zips that failed to
// open, so one bad archive costs only itself.
export type DroppedReadResult = ZipReadResult & {
  ignored: string[];
  unreadable: string[];
  zipCount: number;
  looseCount: number;
};

// Running photo count and byte total for one selection, so the whole-import caps hold across
// several zips and loose files rather than resetting per zip.
export type ImportBudget = { count: number; bytes: number };
export const newImportBudget = (): ImportBudget => ({ count: 0, bytes: 0 });

function overCount(): never {
  throw new ImportLimitError(`That's too many photos (over ${MAX_IMAGE_COUNT}).`);
}

function overTotalBytes(): never {
  throw new ImportLimitError(`Those photos total more than ${Math.round(MAX_TOTAL_BYTES / 1024 / 1024)} MB.`);
}

let warnedMissingSize = false;

// JSZip keeps each entry's uncompressed size in the central directory; reading it lets us reject a
// huge entry before async() decompresses it into memory. It's a private field, so a JSZip upgrade
// could drop it: warn once if that happens, since the caller then silently falls back to the
// post-decompress size check (safe, but the pre-decompress bomb guard is gone). Undefined = fall back.
function uncompressedSize(entry: JSZip.JSZipObject): number | undefined {
  const size = (entry as unknown as { _data?: { uncompressedSize?: number } })._data?.uncompressedSize;
  if (typeof size !== 'number') {
    if (!warnedMissingSize) {
      warnedMissingSize = true;
      console.warn('JSZip _data.uncompressedSize unavailable; zip pre-decompress size guard is degraded.');
    }
    return undefined;
  }
  return size;
}

// Extracts image entries from a dropped zip, skipping directories and macOS hidden/resource-fork
// files. Enforces entry-count, per-image, and cumulative-size limits (checked against the central
// directory before decompression where possible) so a huge or malicious archive can't freeze the
// page; throws ImportLimitError when a whole-import limit is hit. `budget` carries the counts
// already spent by earlier zips and loose files in the same selection.
export async function readZipImages(
  file: File | Blob,
  budget: ImportBudget = newImportBudget(),
): Promise<ZipReadResult> {
  const zip = await JSZip.loadAsync(file);
  const entries = Object.values(zip.files);
  if (entries.length > MAX_ZIP_ENTRIES) {
    throw new ImportLimitError(`That zip has too many entries (over ${MAX_ZIP_ENTRIES}).`);
  }
  const images: ZipImage[] = [];
  const oversized: string[] = [];
  const unsupported: string[] = [];
  for (const entry of entries) {
    if (entry.dir) continue;
    const base = entry.name.split('/').pop() ?? entry.name;
    if (base.startsWith('.')) continue;
    const ext = base.split('.').pop()?.toLowerCase();
    if (!ext) continue;
    if (!IMAGE_EXTENSIONS.has(ext)) {
      if (UNSUPPORTED_IMAGE_EXTENSIONS.has(ext)) unsupported.push(base);
      continue;
    }
    if (budget.count >= MAX_IMAGE_COUNT) overCount();
    // Reject before decompressing when the central directory gives the size.
    const known = uncompressedSize(entry);
    if (known !== undefined) {
      if (known > MAX_IMAGE_BYTES) {
        oversized.push(base);
        continue;
      }
      if (budget.bytes + known > MAX_TOTAL_BYTES) overTotalBytes();
    }
    const blob = await entry.async('blob');
    if (blob.size > MAX_IMAGE_BYTES) {
      oversized.push(base);
      continue;
    }
    budget.bytes += blob.size;
    if (budget.bytes > MAX_TOTAL_BYTES) overTotalBytes();
    budget.count++;
    images.push({ name: base, blob });
  }
  return { images, oversized, unsupported };
}

// Reads one dropped selection of any mix of zips and loose image files into a single result, with
// the photo-count and total-byte caps shared across all of them. Loose files need no extraction, so
// they are classified by extension and size alone. Throws ImportLimitError when a cap is hit.
// `budget` carries what earlier drops in the same import session already spent, so the caps hold
// across drops; it is mutated in place, so pass a copy to discard the spend on failure.
export async function readDroppedFiles(
  files: File[],
  budget: ImportBudget = newImportBudget(),
): Promise<DroppedReadResult> {
  const images: ZipImage[] = [];
  const oversized: string[] = [];
  const unsupported: string[] = [];
  const ignored: string[] = [];
  const unreadable: string[] = [];
  let zipCount = 0;
  let looseCount = 0;

  for (const file of files) {
    const base = file.name.split('/').pop() ?? file.name;
    // macOS writes these alongside a real selection; they are never a photo the user picked.
    if (base.startsWith('.')) continue;
    const ext = base.split('.').pop()?.toLowerCase() ?? '';

    if (ext === 'zip') {
      zipCount++;
      // A zip that fails partway has already spent budget on the entries it did read, and those
      // images are discarded, so it reads against a copy and commits only once it succeeds.
      const zipBudget = { ...budget };
      try {
        const result = await readZipImages(file, zipBudget);
        Object.assign(budget, zipBudget);
        images.push(...result.images);
        oversized.push(...result.oversized);
        unsupported.push(...result.unsupported);
      } catch (e) {
        // A cap applies to the whole selection, so it still stops the read; any other failure is
        // this archive's alone.
        if (e instanceof ImportLimitError) throw e;
        console.warn('Could not read zip', base, e);
        unreadable.push(base);
      }
      continue;
    }

    looseCount++;
    if (!IMAGE_EXTENSIONS.has(ext)) {
      if (UNSUPPORTED_IMAGE_EXTENSIONS.has(ext)) unsupported.push(base);
      else ignored.push(base);
      continue;
    }
    if (budget.count >= MAX_IMAGE_COUNT) overCount();
    if (file.size > MAX_IMAGE_BYTES) {
      oversized.push(base);
      continue;
    }
    if (budget.bytes + file.size > MAX_TOTAL_BYTES) overTotalBytes();
    budget.bytes += file.size;
    budget.count++;
    images.push({ name: base, blob: file });
  }

  return { images, oversized, unsupported, ignored, unreadable, zipCount, looseCount };
}

const MIME_BY_EXT: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
};

export function imageMimeFromName(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  return MIME_BY_EXT[ext] ?? 'application/octet-stream';
}

// Wraps a zip image as an upload File with a proper MIME type (jszip blobs carry no type).
export function toUploadFile(image: ZipImage): File {
  return new File([image.blob], image.name, { type: imageMimeFromName(image.name) });
}

// Long edge of the generated thumbnail. 512 covers a retina field-notes grid cell and stays close to
// the mobile app's own capture thumbnails, so both sources look alike in the grid.
const THUMBNAIL_MAX_PX = 512;

const EXT_BY_MIME: Record<string, string> = {
  'image/jpeg': 'jpeg',
  'image/png': 'png',
  'image/webp': 'webp',
};

// Pica in JS-only mode: its worker build pulls a blob: worker, which the app's CSP makes awkward.
const imageReducer = new ImageBlobReduce({ pica: new ImageBlobReduce.pica({ features: ['js'] }) });

/**
 * Builds a downscaled copy of a photo, uploaded alongside the original and stored as the geo item's
 * `thumbnailPath`. Without one, clients fall back to a per-photo server transcode, which the mobile
 * app skips entirely while offline (blank grid tiles). The original is never re-encoded.
 *
 * Goes through `toBlob` rather than resizing a canvas by hand: image-blob-reduce's jpeg plugins
 * carry the source APP0/APP1 segments (EXIF, so GPS and camera metadata survive) onto the resized
 * copy, and rotate the pixels upright while patching the orientation tag so nothing double-rotates.
 * Output keeps the source format, so a PNG stays lossless.
 *
 * Returns null when the browser can't decode the image, in which case the photo still imports and
 * the transcode fallback applies.
 */
export async function buildThumbnailFile(file: File): Promise<File | null> {
  try {
    const blob = await imageReducer.toBlob(file, { max: THUMBNAIL_MAX_PX });
    const type = blob.type || file.type;
    return new File([blob], thumbnailName(file.name, type), { type });
  } catch (e) {
    console.warn('Could not build a thumbnail for', file.name, e);
    return null;
  }
}

// Mirrors the mobile app's `<name>-thumbnail.<ext>` naming, with the extension the resize actually
// produced so it matches the blob's MIME type.
function thumbnailName(name: string, type: string): string {
  const ext = EXT_BY_MIME[type] ?? name.split('.').pop()?.toLowerCase() ?? 'jpeg';
  return `${name.replace(/\.[^.]+$/, '')}-thumbnail.${ext}`;
}

export type PhotoGps = { lat: number; lng: number; heading: number | null };

// Builds a predicate for whether a photo point falls within the project boundary, buffered by
// toleranceMiles ("nearby" counts as in-area). Fails open (all in-area) when geometry or turf fails.
export function buildProjectAreaChecker(
  geometry: GeoJSON.Geometry | undefined,
  toleranceMiles: number,
): (lng: number, lat: number) => boolean {
  if (!geometry) return () => true;
  try {
    const buffered = buffer(feature(geometry), toleranceMiles, { units: 'miles' });
    if (!buffered) return () => true;
    return (lng, lat) => booleanPointInPolygon([lng, lat], buffered);
  } catch {
    return () => true;
  }
}

// Reads GPS lat/long from a photo's EXIF; returns null when absent or unreadable. Also pulls the
// compass heading the camera faced (EXIF GPSImgDirection) when present — null otherwise.
export async function readGps(blob: Blob): Promise<PhotoGps | null> {
  try {
    const gps = await exifr.gps(blob);
    if (!gps || typeof gps.latitude !== 'number' || typeof gps.longitude !== 'number') return null;
    return { lat: gps.latitude, lng: gps.longitude, heading: await readHeading(blob) };
  } catch {
    return null;
  }
}

// EXIF GPSImgDirection in whole degrees [0, 360). exifr.gps() doesn't return it, so this is a
// targeted read of just that GPS tag. Rounded because compassHeading is an integer column and the
// raw EXIF rational (e.g. 271.33) would fail the int cast. Null when no heading was recorded.
async function readHeading(blob: Blob): Promise<number | null> {
  try {
    const tags = await exifr.parse(blob, { gps: { pick: ['GPSImgDirection'] } });
    const dir = tags?.GPSImgDirection;
    if (typeof dir !== 'number' || Number.isNaN(dir)) return null;
    return Math.round(((dir % 360) + 360) % 360) % 360;
  } catch {
    return null;
  }
}

// Builds a `note` geo-item payload for one geotagged photo. EXIF gives {lat, lng};
// PostGIS point coordinates are [lng, lat] (order swap is intentional). Photos are 'image' geo
// items (not 'note') so the Field Notes viewer renders the photo; the shared note goes on the file.
export function buildFieldNotePayload(params: {
  lat: number;
  lng: number;
  heading?: number | null;
  body?: string;
  photo: UploadedGeoItemPhoto;
  thumbnailPath?: string;
}): CreateGeoItemInput {
  const { lat, lng, heading, body, photo, thumbnailPath } = params;
  const notes = body?.trim() || undefined;

  return {
    type: 'image',
    geometry: { type: 'Point', coordinates: [lng, lat] },
    files: [
      {
        path: photo.path,
        name: photo.name,
        type: photo.type,
        notes,
        compassHeading: heading ?? undefined,
        thumbnailPath,
      },
    ],
  };
}

// Steps a pager index by `delta`, wrapping past either end so Next on the last photo returns to the first.
export const wrapIndex = (index: number, delta: number, length: number) =>
  (((index + delta) % length) + length) % length;
